import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import { fmtDate } from "@/lib/format";

function parseYmd(s) {
  if (!s || typeof s !== "string") return undefined;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
}
function toYmd(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function DatePickerField({ value, onChange, placeholder = "Pick a date", testid, size = "md", className = "" }) {
  const [open, setOpen] = useState(false);
  const date = useMemo(() => parseYmd(value), [value]);
  // MM-DD-YYYY everywhere per product decision — no browser locale wobble.
  const label = useMemo(() => (date ? fmtDate(date) : ""), [date]);
  const h = size === "sm" ? "h-9" : size === "lg" ? "h-12" : "h-11";
  return (
    // `modal` is critical: DatePickerField is often used INSIDE a Radix
    // Dialog (Audition Slots, Rehearsal Editor, Season Planner, form gate…).
    // Radix Dialogs are modal and put `pointer-events: none` on the body
    // while open, which blocks clicks on any popover portaled outside the
    // dialog tree. Making the Popover itself modal gives it its own pointer
    // context so day cells stay clickable regardless of the parent.
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`w-full ${h} px-3 rounded-md bg-[#121212] border border-white/10 hover:border-white/20 text-left text-sm inline-flex items-center gap-2 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold ${className}`}
        >
          <CalendarIcon className="w-4 h-4 text-zinc-400 shrink-0" />
          <span className={value ? "text-white truncate" : "text-zinc-500 truncate"}>{value ? label : placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        // z-[120] beats the PendingFormsGate (z-100) and FormBuilder
        // preview overlay (z-90). Without this the popover renders in a
        // portal at z-50 and gets buried behind the gate so users can't
        // pick a date while acknowledging a required form.
        className="w-auto p-0 bg-[#121212] border-white/10 text-white z-[120]"
      >
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => { onChange(toYmd(d)); setOpen(false); }}
          initialFocus
        />
        {value && (
          <div className="p-2 border-t border-white/10 flex justify-end">
            <button type="button" onClick={() => { onChange(""); setOpen(false); }} className="text-xs text-zinc-400 hover:text-white">Clear</button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
